// ============================================================
// Mondes (NationsGlory) : constantes partagées avec le satellite
// ============================================================
// Mêmes valeurs que WorldsValidation côté satellite. Le serveur reste l'autorité : ces règles ne
// servent qu'à prévenir à la frappe, un refus serveur est toujours affiché tel quel.
// ============================================================

/** Drapeau : 26 x 13, pixel i = ligne*26 + colonne, ligne 0 en haut. */
export const FLAG_W = 26;
export const FLAG_H = 13;
export const FLAG_SIZE = FLAG_W * FLAG_H;

/** Palette du drapeau, ordre EXACT de SettingsGUI.flag_colorList du mod (la valeur stockée est l'index). */
export const FLAG_PALETTE = [
  '#F3E500', '#FDC60A', '#F38F1F', '#EB6120', '#E32322', '#C4047E', '#6E398B', '#454E99', '#2B70B1',
  '#0696BB', '#008E5C', '#8DBB25', '#FFFFFF', '#050505', '#603913', '#A7A7A7', '#A0410D', '#C69C6D',
];

/** Index du blanc : un drapeau null est blanc partout. */
export const FLAG_WHITE = 12;

/** Couleurs proposées pour un pays ou une géo-région. */
export const SUGGESTED_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E', '#10B981', '#14B8A6', '#06B6D4',
  '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899', '#F43F5E', '#64748B',
];
export const DEFAULT_COLOR = '#3B82F6';

/** Plafond de cellules par opération de peinture (au-delà : 413). */
export const MAX_OP_CELLS = 50000;
/** Fenêtre du remplissage autour du chunk cliqué. */
export const FILL_RADIUS = 200;
export const MAX_CHUNK_COORD = 1875000;
export const MAX_BLOCK_COORD = 30000000;

/** Taille max du pinceau, en chunks (carré). */
export const MAX_BRUSH = 32;

/** Rafraîchissement de l'état (versions) quand l'onglet est visible. */
export const POLL_MS = 10000;

/** Altitude du /tp proposé au clic droit. */
export const TP_ALTITUDE = 200;

/** Nom fondable (slug d'un pays) : 3 à 17 caractères, règle NG ∩ FactionService.IsValidName. */
export const SLUG_RE = /^[A-Za-z][A-Za-z0-9_-]{2,16}$/;
export const SLUG_HINT = '3 à 17 caractères, lettres, chiffres, - et _, commence par une lettre';
/** slugEn, empireNameFr, empireNameEn (vide = null). */
export const NAME_RE = /^[A-Za-z][A-Za-z0-9_-]{0,31}$/;
export const NAME_HINT = '1 à 32 caractères, lettres, chiffres, - et _, commence par une lettre';
export const GEO_SLUG_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
export const LEVEL_NAME_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
export const BIOME_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;
export const COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
export const ISO_RE = /^[A-Za-z]{1,3}$/;

/** tileUrl : null, ou http(s) de 500 caractères au plus portant {z}, {x} et {y}. */
export function tileUrlError(url: string): string | null {
  const u = url.trim();
  if (!u) return null;
  if (u.length > 500) return '500 caractères au plus';
  if (!/^https?:\/\//.test(u)) return 'Doit commencer par http:// ou https://';
  if (!u.includes('{z}') || !u.includes('{x}') || !u.includes('{y}')) return 'Doit contenir {z}, {x} et {y}';
  return null;
}

/** « #rrggbb » ou « rrggbb » → « #RRGGBB », sinon null. */
export function normColor(v: string): string | null {
  const t = v.trim();
  const c = t.startsWith('#') ? t : `#${t}`;
  return COLOR_RE.test(c) ? c.toUpperCase() : null;
}

/** « #RRGGBB » → [r, g, b]. */
export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1, 7), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Date ISO → « 25/09/2026 10:00 » (heure locale). */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** 427613 → « 427 613 ». */
export function fmtInt(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('fr-FR') : '';
}

// ---- Préférences locales (par navigateur) ----
/**
 * Lit une préférence JSON du localStorage (onglet, couches de la carte, encart replié...). Stockage
 * absent, bloqué (navigation privée) ou valeur illisible : `fallback`. Jamais d'exception.
 */
export function loadPref<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Écrit une préférence JSON ; un stockage indisponible est ignoré sans bruit. */
export function savePref(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch { /* stockage indisponible : préférence non mémorisée */ }
}

// ---- Énergie (pétrole, gaz) : même rendu que EnergyFormat.number du jeu (NationsGui) ----
const ENERGY_FLOOR = 100000;
const ENERGY_DIVISORS = [1e12, 1e9, 1e6, 1e3];
const ENERGY_SUFFIXES = ['T', 'B', 'M', 'k'];
const ENERGY_PLAIN = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });
const ENERGY_MANTISSA = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/**
 * En dessous de 100 000 : nombre entier à la française (« 99 999 ») ; au-delà, une décimale et un
 * suffixe k, M, B ou T (« 254,3k », « 2,5M »). Même seuil et mêmes suffixes que le jeu, pour que le
 * panel et les écrans en jeu affichent le même nombre. Valeur non finie : « 0 ».
 */
export function fmtEnergy(n: number): string {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  if (abs < ENERGY_FLOOR) return sign + ENERGY_PLAIN.format(abs);
  let i = ENERGY_DIVISORS.findIndex((d) => abs >= d);
  if (i < 0) i = ENERGY_DIVISORS.length - 1;
  // Mantisse arrondie AVANT le choix final du suffixe : 999 999 donne « 1M », pas « 1 000k ».
  let mantissa = Math.round((abs / ENERGY_DIVISORS[i]) * 10) / 10;
  if (mantissa >= 1000 && i > 0) {
    i -= 1;
    mantissa = Math.round((abs / ENERGY_DIVISORS[i]) * 10) / 10;
  }
  return sign + ENERGY_MANTISSA.format(mantissa) + ENERGY_SUFFIXES[i];
}

// ---- Noms ----
/** Ligatures que la décomposition NFD ne sépare pas. */
const LIGATURES: Record<string, string> = { 'œ': 'oe', 'Œ': 'Oe', 'æ': 'ae', 'Æ': 'Ae', 'ß': 'ss' };

/** Retire les accents (NFD) et remplace les ligatures courantes. */
function plainLatin(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[œŒæÆß]/g, (c) => LIGATURES[c] ?? c);
}

/**
 * Nom saisi → identifiant du jeu en PascalCase (« empire afghan » → « EmpireAfghan »,
 * « Côte d'Ivoire » → « CoteDIvoire ») : accents retirés, découpe sur les espaces et la ponctuation
 * (sauf _ et -), capitale initiale à chaque mot. Une valeur déjà conforme à NAME_RE est rendue telle
 * quelle (seulement rognée). Ne tronque pas : la validation signale un nom trop long.
 */
export function toIdentifier(s: string): string {
  const t = s.trim();
  if (!t || NAME_RE.test(t)) return t;
  return plainLatin(t)
    .split(/[^A-Za-z0-9_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}

/**
 * Nom affiché d'un biome → nom technique conforme à BIOME_NAME_RE (« Désert rouge » →
 * « desert_rouge ») : minuscules sans accents, tout ce qui n'est pas lettre ou chiffre devient « _ »,
 * commence par une lettre, 64 caractères au plus. Rien d'exploitable : chaîne vide.
 */
export function toSnakeName(s: string): string {
  return plainLatin(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .slice(0, 64)
    .replace(/_+$/, '');
}

/** Drapeau base64 (338 octets) → index de palette ; null ou invalide = blanc. */
export function decodeFlag(b64: string | null | undefined): Uint8Array {
  const out = new Uint8Array(FLAG_SIZE).fill(FLAG_WHITE);
  if (!b64) return out;
  try {
    const bin = atob(b64);
    if (bin.length !== FLAG_SIZE) return out;
    for (let i = 0; i < FLAG_SIZE; i++) {
      const v = bin.charCodeAt(i);
      out[i] = v < FLAG_PALETTE.length ? v : FLAG_WHITE;
    }
  } catch { /* base64 illisible : blanc */ }
  return out;
}

/** Index de palette → base64 standard avec padding (452 caractères). */
export function encodeFlag(pixels: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < pixels.length; i++) bin += String.fromCharCode(pixels[i]);
  return btoa(bin);
}

/** Couleur de palette la plus proche (distance L2 sur RGB). */
export function nearestPaletteIndex(r: number, g: number, b: number): number {
  let best = 0;
  let bestD = Infinity;
  FLAG_PALETTE.forEach((hex, i) => {
    const [pr, pg, pb] = hexToRgb(hex);
    const d = (pr - r) ** 2 + (pg - g) ** 2 + (pb - b) ** 2;
    if (d < bestD) { bestD = d; best = i; }
  });
  return best;
}
